import { DocumentType } from "encamp-prisma/generated/browser";
import invariant from "tiny-invariant";

import { TierIIRecordKind } from "../generated-graphql";
import { prettyPrintDocumentType } from "../utils/prettyPrintDocumentType";
import { getTenantNamingOverride } from "./tenantNaming";

export { prettyPrintDocumentType } from "../utils/prettyPrintDocumentType";
export { deriveDocumentName } from "./deriveDocumentName";
export {
  CONTAINER_TRACKING_ENRICHMENT_DOCUMENT_TYPES,
  MANIFEST_VIEWS_V2_ENRICHMENT_DOCUMENT_TYPES,
  WASTE_MODULE_ENRICHMENT_DOCUMENT_TYPES,
  documentEnrichmentIsEntitled,
} from "./documentEnrichmentEntitlement";
export {
  DOCUMENT_ENRICHMENT_ALLOWED_EXTENSIONS,
  DOCUMENT_ENRICHMENT_SKIP_PREFIXES,
  INCLUDE_AUTHORED_PREFIXES,
  RERUN_FAILED_DOCUMENTS,
  buildSkipWhere,
  enrichmentSkipUserMessage,
  getEnrichmentSkipReason,
  shouldSkip,
} from "./documentEnrichmentSkip";
export type {
  DocumentEnrichmentSkipInput,
  DocumentEnrichmentSkipOptions,
  DocumentEnrichmentSkipReason,
} from "./documentEnrichmentSkip";
export {
  getNamingFormatHelpText,
  getTenantNamingOverride,
  TENANT_DOCUMENT_NAMING_OVERRIDES,
} from "./tenantNaming";
export type { DocumentNamingContext, DocumentNamingOverride } from "./tenantNaming";

export function mapTierIIRecordKindToDocumentType(kind: TierIIRecordKind): DocumentType {
  const kindToDocumentType = {
    AST_DOCUMENTATION: DocumentType.ASTDocumentation,
    DIKE_DESCRIPTION: DocumentType.DikeDescription,
    EMERGENCY_RESPONSE_PLAN: DocumentType.EmergencyResponsePlan,
    HAZARDOUS_MATERIALS_INVENTORY_STATEMENT: DocumentType.HazardousMaterialsInventoryStatement,
    HAZARDOUS_MATERIALS_MANAGEMENT_PLAN: DocumentType.HazardousMaterialsManagementPlan,
    HAZARDOUS_MATERIALS_CONTINGENCY_PLAN_OR_QUICK_GUIDE: DocumentType.Other,
    INVOICE: DocumentType.Invoice,
    OTHER: DocumentType.Other,
    RECEIPT: DocumentType.Receipt,
    REFERENCE_IMAGE: DocumentType.ReferenceImage,
    REPORT: DocumentType.Report,
    SAFEGUARD_MEASURES: DocumentType.SafeguardMeasures,
    SCREENSHOT: DocumentType.Screenshot,
    SDS: DocumentType.SafetyDataSheet,
    SITE_COORDINATE_ABBREVIATION: DocumentType.SiteCoordinateAbbreviation,
    SITE_PLAN: DocumentType.SitePlan,
    TRAINING_PLAN: DocumentType.TrainingPlan,
    UST_DOCUMENTATION: DocumentType.USTDocumentation,
  };

  return kindToDocumentType[kind] ?? DocumentType.Other;
}

/**
 * Document types that are set exclusively by backend/system ingest and must
 * never be user-assignable. Excluded from document-type pickers and rejected by
 * the document upsert resolver. (RCRAManifestPrintedCopy is the EPA printed-copy
 * scan warehoused by the RCRAInfo poll. HazardousWasteContainerLabel is the
 * machine-generated RCRA/DOT container label PDF produced by the pdf-generator
 * lambda.)
 */
export const SYSTEM_MANAGED_DOCUMENT_TYPES: DocumentType[] = [
  DocumentType.RCRAManifestPrintedCopy,
  DocumentType.HazardousWasteContainerLabel,
  // Written only by generateWasteProfileSummary. A user picking it would
  // label an upload as something Encamp rendered, and the generator would
  // later overwrite that upload at the stream's stable key.
  DocumentType.WasteStreamProfileSummary,
];

const systemManagedDocumentTypeValues: ReadonlySet<string> = new Set(
  SYSTEM_MANAGED_DOCUMENT_TYPES.map((t) => t.toString())
);

/**
 * String-based check so it works against either the Prisma or GraphQL
 * DocumentType enum (their values are identical strings).
 */
export function isSystemManagedDocumentType(documentType: string | null | undefined): boolean {
  if (!documentType) return false;
  return systemManagedDocumentTypeValues.has(documentType);
}

/**
 * Document types the nightly AI-enrichment cron must never auto-extract.
 *
 * Deliberately distinct from SYSTEM_MANAGED_DOCUMENT_TYPES: that list governs
 * whether a type is user-assignable (pickers + upsert guard), this one governs
 * whether the cron auto-parses it. A type could be system-managed yet still
 * auto-parseable, or auto-extraction-excluded yet user-assignable — they answer
 * different questions and are kept separate even though they happen to overlap.
 * An explicit documentId-scoped parse request bypasses this exclusion — see
 * buildDocumentWhereClause.
 *
 * Manifest documents are parsed on demand, never by the nightly sweep:
 *   - the manifest upload flow dispatches an explicit extraction immediately, and
 *   - staff can parse any document on demand from the Manifest Documents grid.
 * Excluding them lets a manifest be attached *without* parsing (it simply stays
 * unparsed until someone parses it) rather than being silently auto-parsed
 * overnight. RCRAManifestPrintedCopy is also system-managed; RCRAManifest is not.
 * HazardousWasteContainerLabel is machine-generated by the pdf-generator lambda
 * and has no free-text content for the AI enrichment cron to extract.
 *
 * The generated waste profile summary is excluded for a different reason: it is
 * our own render of data we already hold as columns, so extracting it would
 * describe our output back to us and learn nothing. Worse, it is not inert.
 * Extraction rewrites Document.documentType to whatever the model classifies
 * the PDF as (storeExtractedMetadata), and the generator finds an existing
 * summary to overwrite by that type — so a reclassified row goes unrecognised
 * and the next regenerate writes a second document at the same storage key.
 *
 * The uploaded TSDF sheet (WasteStreamProfile) is deliberately NOT excluded. What
 * must not happen automatically is the *profile extraction* — reading a sheet into
 * a characterization candidate nobody asked for, from a prompt no reviewer has
 * validated. That is gated in the pipeline on the request naming the document,
 * not by keeping the sheet out of enrichment altogether. Excluding the type here
 * would also cost a DocumentAI tenant the general metadata and embedding that make
 * the sheet findable in document search, which has nothing to do with the profile.
 */
export const AUTO_EXTRACTION_EXCLUDED_DOCUMENT_TYPES: DocumentType[] = [
  DocumentType.RCRAManifest,
  DocumentType.RCRAManifestPrintedCopy,
  DocumentType.HazardousWasteContainerLabel,
  DocumentType.WasteStreamProfileSummary,
];

const autoExtractionExcludedDocumentTypeValues: ReadonlySet<string> = new Set(
  AUTO_EXTRACTION_EXCLUDED_DOCUMENT_TYPES.map((t) => t.toString())
);

/**
 * String-based check so it works against either the Prisma or GraphQL
 * DocumentType enum (their values are identical strings).
 */
export function isAutoExtractionExcludedDocumentType(
  documentType: string | null | undefined
): boolean {
  if (!documentType) return false;
  return autoExtractionExcludedDocumentTypeValues.has(documentType);
}

/**
 * Document types that are renewable permits (generic + specific subtypes from
 * classification). Used to gate permit-renewal task creation / UI offers.
 *
 * Excludes applications, emissions tables, and approval letters — those are
 * related correspondence, not the permit instrument itself.
 */
export const RENEWABLE_PERMIT_DOCUMENT_TYPES: DocumentType[] = [
  DocumentType.Permit,
  DocumentType.AirPermit,
  DocumentType.NPDESPermit,
  DocumentType.ConfinedFeedingOperationPermit,
];

const renewablePermitDocumentTypeValues: ReadonlySet<string> = new Set(
  RENEWABLE_PERMIT_DOCUMENT_TYPES.map((t) => t.toString())
);

/**
 * String-based check so it works against either the Prisma or GraphQL
 * DocumentType enum (their values are identical strings).
 */
export function isRenewablePermitDocumentType(documentType: string | null | undefined): boolean {
  if (!documentType) return false;
  return renewablePermitDocumentTypeValues.has(documentType);
}

/**
 * This is a list of DocumentTypes that are considered for submitting a Tier II report
 * regardless of jurisdiction; a list of types of documents that _might_ be
 * needed to fulfill a report.
 */
export const KnownSubmissionDocumentTypes: DocumentType[] = [
  DocumentType.ASTDocumentation,
  DocumentType.DikeDescription,
  DocumentType.EmergencyResponsePlan,
  DocumentType.HazardousMaterialsContingencyPlanOrQuickGuide,
  DocumentType.HazardousWasteContingencyPlan,
  DocumentType.SafeguardMeasures,
  DocumentType.SiteCoordinateAbbreviation,
  DocumentType.SitePlan,
  DocumentType.TrainingPlan,
  DocumentType.USTDocumentation,
  // CERS HMBP document types
  DocumentType.USTMonitoringSitePlan,
  DocumentType.USTResponsePlan,
  DocumentType.USTOwnerOperatorWrittenAgreement,
  DocumentType.USTLetterFromChiefFinancialOfficer,
  DocumentType.OwnerStatementOfDesignatedUSTOperatorCompliance,
  DocumentType.USTCertificationOfFinancialResponsibility,
  DocumentType.RecyclableMaterialsReportDocumentation,
  DocumentType.HazardousWasteTankClosureCertificate,
  DocumentType.AboveGroundPetroleumStorageActDocumentation,
  DocumentType.AboveGroundSeparationContainmentAndMonitoringPlan,
  DocumentType.SpillPreventionControlAndCountermeasurePlan,
  DocumentType.LocalInformationDocument,
];

/**
 * Removes duplicate extensions from a filename.
 *
 * For example, if the filename is "report.pdf.pdf", this function will return "report.pdf".
 */
export function removeDuplicateExtension(filename: string) {
  // Split the filename by dots
  const parts = filename.split(".");

  // If we have 3 or more parts and the last two are identical
  if (parts.length >= 3 && parts[parts.length - 1] === parts[parts.length - 2]) {
    // Remove the last extension and join back together
    parts.pop();
    return parts.join(".");
  }

  // Return original filename if no duplicate extension found
  return filename;
}

export function convertBytesToMegabytes(bytes: number | null): number | null {
  if (bytes === null) return null;
  if (bytes === 0) return 0;

  const megabytes = bytes / (1024 * 1024);

  // If the value is nonzero but rounds to 0, show a minimum value
  if (megabytes > 0 && megabytes < 0.000001) {
    return 0.000001;
  }

  return Number(megabytes.toFixed(6));
}

/**
 * Replaces all characters that aren't letters, numbers, or dashes with underscores.
 * This ensures the string is safe to use as a filename.
 * @example "Hello, World!" -> "Hello_World_"
 */
export function cleanStringForFilesystem(str: string): string {
  // [^a-zA-Z0-9-] means "match any character that is NOT a-z, A-Z, 0-9, or -"
  // and then replace any multiple underscores with a single underscore.
  return str.replace(/[^a-zA-Z0-9-]/g, "_").replace(/_+/g, "_");
}

// Parenthesized acronyms like "(SPCC)" help in pickers but only clutter a filename.
export function documentTypeFilenameSegment(documentType: DocumentType): string {
  return prettyPrintDocumentType(documentType).replace(/\s*\([^)]*\)/g, "");
}

/**
 * Formats a document name based on its type, facility name, date, and other parameters.
 *
 * Examples:
 * A certification for my facility on 2024-01-01 would be:
 * - "Certification_My_Facility_2024-01-01.pdf"
 * A site plan for my activity on the report for my facility on 2024-01-01 would be:
 * - "Site_Plan_My_Facility_2024-01-01.pdf"
 * A Report for my facility on 2024-01-01 would be:
 * - "Report_My_Facility_2024-01-01.pdf"
 * A Receipt for multiple facilities on 2024-01-01 would be:
 * - "Receipt_2024-01-01.pdf"
 *
 * If `tenantId` has a registered entry in TENANT_DOCUMENT_NAMING_OVERRIDES,
 * the override fully owns the title format and `titlePrefix` is ignored.
 */
export function formatDocumentName({
  document,
  facilityName,
  date,
  titlePrefix,
  tenantId,
  reportingYear,
  documentName,
}: {
  document: Pick<
    { documentType: DocumentType; fileExtension: string },
    "documentType" | "fileExtension"
  >;
  facilityName: string;
  date?: Date;
  titlePrefix?: string;
  tenantId?: string | null;
  reportingYear?: number | null;
  documentName?: string;
}): string {
  invariant(document.documentType, "Document type is required to generate a document name");
  const dateToUse = date ?? new Date();
  const override = getTenantNamingOverride(tenantId);
  if (override) {
    return override({
      documentType: document.documentType,
      facilityName: facilityName ?? "",
      reportingYear: reportingYear ?? null,
      date: dateToUse,
      fileExtension: document.fileExtension,
      documentName,
    });
  }
  const formattedDocumentType = documentTypeFilenameSegment(document.documentType);
  const formattedDate = dateToUse.toISOString().split("T")[0];
  const combined = [titlePrefix, formattedDocumentType, facilityName, formattedDate]
    .filter((x) => !!x && x.length > 0)
    .join("_");
  return `${cleanStringForFilesystem(combined)}.${document.fileExtension}`;
}
